/**
 * socket.js — singleton Socket.io client
 *
 * Import this wherever you need the socket (chat AND notifications).
 * It creates one connection and reuses it across the app.
 *
 * Usage:
 *   import { getSocket } from "../socket/socket";
 *   const socket = getSocket(userId);
 */

import { io } from "socket.io-client";

let _socket = null;

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  (process.env.NODE_ENV === "production"
    ? "https://tutorslinks.com"
    : "http://localhost:8000");

/**
 * Returns (and lazily creates) the singleton socket.
 * Call once with userId on login; everywhere else call with no args.
 *
 * @param {string} userId - MongoDB user _id string (required on first call)
 */
export function getSocket(userId) {
  if (!_socket) {
    if (!userId) throw new Error("getSocket: userId required for initial connection");

    _socket = io(SOCKET_URL, {
      auth: { userId },
      transports: ["websocket"],
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    _socket.on("connect", () =>
      console.log("🔌 Frontend Socket connected:", _socket.id)
    );
    _socket.on("connect_error", (err) =>
      console.error("🔌 Socket error:", err.message)
    );
    _socket.on("disconnect", (reason) =>
      console.log("🔌 Socket disconnected:", reason)
    );
  }

  return _socket;
}

/**
 * Disconnects and destroys the singleton.
 * Call on logout.
 */
export function disconnectSocket() {
  if (_socket) {
    _socket.disconnect();
    _socket = null;
  }
}
